import React, { memo } from 'react'
import classNames from 'classnames'
import moment from 'moment-timezone'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ManagerTag } from 'transfix-ui/components/ManagerTag'
import { AlertIcon } from 'transfix-icons'
import { parseMessage } from 'utils/parseMessage'
import { FORMATS } from 'constants/datetime'
import { INVALID_NUMBER_ERROR_MESSAGES, OPT_OUT_MESSAGES } from 'constants/sms'
import { USER_TYPE } from 'constants/userTypes'

export interface ISMSContact {
  userType: USER_TYPE
  firstName: string
  lastName: string
}

export interface ISMSProps {
  id: number
  createdAt: string
  message?: string
  sentByUserType?: USER_TYPE
  from?: ISMSContact
  shipmentId: number
  last: boolean
  sendError?: string
  to?: ISMSContact
}

export const SMS = memo<ISMSProps>(
  ({ createdAt, from, id, last, message, sendError, sentByUserType, shipmentId, to }) => {
    const toFirst = to?.firstName
    const toLastName = to?.lastName
    const sentByUs = ['admin', 'system'].includes(sentByUserType ?? from?.userType ?? '')
    const fullName = `${from?.firstName} ${from?.lastName}`

    const formatErrorMessage = (unformattedMessage?: string) => {
      if (!unformattedMessage) return ''
      const toFullName = `${toFirst} ${toLastName}`
      let formattedErrorMessage = 'Delivery failed. Please try again.'
      INVALID_NUMBER_ERROR_MESSAGES.forEach(errorMessage => {
        if (unformattedMessage.includes(errorMessage)) {
          formattedErrorMessage = `Delivery failed. ${toFullName} has an invalid phone number. Please update phone number.`
        }
      })
      OPT_OUT_MESSAGES.forEach(errorMessage => {
        if (unformattedMessage.includes(errorMessage)) {
          formattedErrorMessage = `Delivery failed. ${toFullName} has opted out of receiving text messages. Please call or email instead.`
        }
      })
      return formattedErrorMessage
    }
    const errorMessage = formatErrorMessage(sendError)

    const classes = classNames(
      { pv1: last },
      { pv4: !last },
      { 'sent-message': sentByUs },
      { 'received-message': !sentByUs }
    )

    return (
      <div className={classes}>
        <div className='message-content mb2'>
          <div className='message-sender'>
            {sendError ? (
              <AlertIcon className='tag tag-round' />
            ) : (
              <ManagerTag
                type='owner'
                fullName={fullName}
                onlyName
                align={sentByUs ? 'right' : undefined}
              />
            )}
          </div>
          <div className='message pa4'>
            <span
              data-test={`message-${id}`}
              dangerouslySetInnerHTML={{ __html: parseMessage(message) }}
            />{' '}
            {/* eslint-disable-line react/no-danger */}
          </div>
        </div>
        <div className={`message-meta ${last ? 'pv1' : 'pv2'}`}>
          <div>
            {fullName} on{' '}
            {moment.tz(createdAt, FORMATS.defaultTimeZone).format(FORMATS.datetimeInputFormat)}
          </div>
          {shipmentId && (
            <div>
              <a
                href={`${__ROOT__}/shipment-details/${shipmentId}?tab=scheduling`}
                target='_blank'
                rel='noopener noreferrer'>
                Shipment {shipmentId}
              </a>
            </div>
          )}
        </div>
        {sendError && (
          <InlineMessage message={errorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} fullWidth />
        )}
      </div>
    )
  }
)
